import {
  freeAttribute,
  handedOverAttribute,
  handoverCardsAttribute,
  handoverDueAttribute,
  limitedAttribute,
  choicesAttribute,
  choicesFrom,
  repliesAttribute,
  triesAttribute,
  uncheckedAttribute,
  missedAttribute,
  passAttribute,
  queueAttribute,
  recordAttribute,
  stageAttribute,
  versionAttribute,
  revisionAttribute,
} from "../content/lianxi/exercise-fields.js";
import { tileSaidAttribute } from "./fuxi-tile-moves.js";
import { handoverCountOf } from "./lianxi-handover.js";
import { sentenceSaidAttribute } from "./lianxi-yufa-sounds.js";

/**
 * A place the handler answered with, written onto an Exercise's block as the
 * attributes the page starts from. `./lianxi-place.ts` asks for it.
 */

/** Whole numbers, or nothing where the value is not a list of them. */
function wholeNumbers(value: unknown): readonly number[] | undefined {
  return Array.isArray(value) &&
    value.every((each) => Number.isInteger(each) && Number(each) >= 0)
    ? (value as number[])
    : undefined;
}

/** A drill's places, written onto each character's item. */
function writeDrill(block: HTMLElement, places: unknown): boolean {
  if (!Array.isArray(places)) {
    return false;
  }

  for (const place of places as readonly Record<string, unknown>[]) {
    const item = [
      ...block.querySelectorAll<HTMLElement>("[data-cb-xiezi-character]"),
    ].find((each) => each.dataset["cbXieziCharacter"] === place["character"]);

    if (item !== undefined) {
      item.dataset[stageAttribute] = String(Number(place["stage"]) || 0);
      item.dataset[passAttribute] = String(Number(place["pass"]) || 0);
    }
  }

  return true;
}

/** A string, or the empty string for anything else. */
function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/**
 * What each item plays, written onto its form and its tiles: the sentence on
 * the item, and each word on the tile naming its place in the sentence.
 */
function writeSounds(block: HTMLElement, sounds: unknown): void {
  const forms = [
    ...block.querySelectorAll<HTMLFormElement>("form[data-cb-yufa-item]"),
  ];

  if (!Array.isArray(sounds)) {
    return;
  }

  for (const [at, form] of forms.entries()) {
    const said = (sounds[at] ?? {}) as Record<string, unknown>;
    const tiles = Array.isArray(said["tiles"]) ? said["tiles"] : [];

    form.setAttribute(sentenceSaidAttribute, text(said["sentence"]));

    for (const tile of form.querySelectorAll<HTMLElement>(
      "[data-cb-yufa-place]",
    )) {
      const word = text(tiles[Number(tile.dataset["cbYufaPlace"])]);

      if (word !== "") {
        tile.setAttribute(tileSaidAttribute, word);
      }
    }
  }
}

/** A grammar exercise's queue and missed items, written onto the block. */
function writeYufa(
  block: HTMLElement,
  answer: Record<string, unknown>,
): boolean {
  const queue = wholeNumbers(answer["queue"]);
  const missed = wholeNumbers(answer["missed"]);

  if (queue === undefined || missed === undefined) {
    return false;
  }

  const version = block.dataset[versionAttribute];
  if (version !== undefined && answer["version"] !== version) {
    return false;
  }
  if (Number.isInteger(answer["revision"]) && Number(answer["revision"]) >= 0) {
    block.dataset[revisionAttribute] = String(answer["revision"]);
  }

  block.dataset["cbYufaPractice"] = JSON.stringify(answer["practice"] ?? null);
  block.dataset[queueAttribute] = queue.join(",");
  block.dataset[missedAttribute] = missed.join(",");
  block.dataset["cbYufaCommented"] = String(answer["commented"] === true);
  writeSounds(block, answer["sounds"]);

  return true;
}

/**
 * A reading Exercise's queue and missed questions, written onto the block,
 * and a 拼字 Exercise's queue and missed characters, and a 语音答题 Exercise's
 * queue and missed words, which take the same shape.
 */
function writeYuedu(
  block: HTMLElement,
  answer: Record<string, unknown>,
): boolean {
  const queue = wholeNumbers(answer["queue"]);
  const missed = wholeNumbers(answer["missed"]);
  const version = block.dataset[versionAttribute];

  if (
    queue === undefined ||
    missed === undefined ||
    (version !== undefined && answer["version"] !== version)
  ) {
    return false;
  }

  block.dataset[queueAttribute] = queue.join(",");
  block.dataset[missedAttribute] = missed.join(",");

  const choices = choicesFrom(
    answer["choices"],
    Number(block.dataset["cbItems"] ?? ""),
  );

  if (choices !== undefined) {
    block.dataset[choicesAttribute] = choices.join(",");
  }

  writeReplies(block, answer);

  return true;
}

/** A reading Exercise's written replies, where the answer holds any (CB-77). */
function writeReplies(
  block: HTMLElement,
  answer: Record<string, unknown>,
): void {
  const replies = answer["replies"];
  const tries = wholeNumbers(answer["tries"]);
  const unchecked = wholeNumbers(answer["unchecked"]);

  if (
    !Array.isArray(replies) ||
    !replies.every((each) => typeof each === "string") ||
    tries === undefined ||
    unchecked === undefined
  ) {
    return;
  }

  block.dataset[repliesAttribute] = JSON.stringify(replies);
  block.dataset[triesAttribute] = tries.join(",");
  block.dataset[uncheckedAttribute] = unchecked.join(",");
}

/**
 * The place an answer holds, written onto the block, or nothing written.
 *
 * An answer saying the reader has no new Exercise left to start writes no
 * place. It marks the block limited instead, and the page runs the taste a
 * visitor is given (`./lianxi-gate.ts`), keeping nothing.
 */
export function adopt(block: HTMLElement, answer: unknown): void {
  const read = (answer ?? {}) as Record<string, unknown>;
  const { kind, record } = read;

  if (read["limited"] === true) {
    block.dataset[limitedAttribute] = "";

    return;
  }

  const written =
    typeof record === "string" &&
    record !== "" &&
    (kind === "xiezi"
      ? writeDrill(block, read["places"])
      : kind === "yufa"
        ? writeYufa(block, read)
        : (kind === "yuedu" || kind === "pinzi" || kind === "shuoci") &&
          writeYuedu(block, read));

  if (!written) {
    return;
  }

  block.dataset[recordAttribute] = record;

  if (read["handedOver"] === true) {
    block.dataset[handedOverAttribute] = "";
  }

  const count = handoverCountOf(
    read["handedOverCards"],
    read["handedOverDueAt"],
  );

  if (count !== undefined) {
    block.dataset[handoverCardsAttribute] = String(count.cards);
    block.dataset[handoverDueAttribute] = count.dueAt.toISOString();
  }

  if (typeof read["startsLeft"] === "number") {
    block.dataset[freeAttribute] = "";
  }
}
